import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { cn } from "@/lib/utils";
import { FieldError, fieldErrorId } from "./FieldError";
import {
  fieldFocusVisibleClass,
  fieldInvalidClass,
  fieldRadius,
} from "./field-variants";

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  error?: string;
  autoFocus?: boolean;
  id?: string;
  "aria-label"?: string;
}

function digitsOnly(raw: string, max: number): string {
  return raw.replace(/\D/g, "").slice(0, max);
}

export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  error,
  autoFocus = false,
  id,
  "aria-label": ariaLabel = "One-time code",
}: OtpInputProps) {
  const reactId = useId();
  const groupId = id ?? `otp-${reactId}`;
  const errorId = fieldErrorId(groupId);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = digitsOnly(value, length).padEnd(length, " ").slice(0, length);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    inputsRef.current[0]?.focus();
  }, [autoFocus, disabled]);

  const setDigitAt = useCallback(
    (index: number, char: string) => {
      const current = digitsOnly(value, length);
      const nextChars = current.padEnd(length, " ").split("");
      nextChars[index] = char;
      const next = digitsOnly(nextChars.join(""), length);
      onChange(next);
      return next;
    },
    [length, onChange, value],
  );

  const focusAt = (index: number) => {
    const el = inputsRef.current[index];
    if (!el) return;
    el.focus();
    el.select();
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (disabled) return;
    const pasted = digitsOnly(event.clipboardData.getData("text"), length);
    if (!pasted) return;
    onChange(pasted);
    focusAt(Math.min(pasted.length, length) - 1);
  };

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (disabled) return;
    if (event.key === "Backspace") {
      event.preventDefault();
      const current = digitsOnly(value, length);
      if (current[index]) {
        setDigitAt(index, "");
        return;
      }
      if (index > 0) {
        setDigitAt(index - 1, "");
        focusAt(index - 1);
      }
      return;
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusAt(index - 1);
    }
    if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  return (
    <div>
      <div
        id={groupId}
        role="group"
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className="flex items-center gap-2"
      >
        {Array.from({ length }, (_, index) => {
          const char = digits[index]?.trim() ?? "";
          return (
            <input
              key={`${groupId}-${index}`}
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={1}
              disabled={disabled}
              value={char}
              aria-label={`${ariaLabel} digit ${index + 1}`}
              onPaste={handlePaste}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onChange={(event) => {
                const nextChar = digitsOnly(event.target.value, 1);
                if (!nextChar) {
                  setDigitAt(index, "");
                  return;
                }
                const next = setDigitAt(index, nextChar);
                if (index < length - 1 && next.length > index) {
                  focusAt(index + 1);
                }
              }}
              onFocus={(event) => event.currentTarget.select()}
              className={cn(
                "h-12 w-10 shrink-0 border border-surface-200 bg-surface-0 text-center text-lg font-semibold tabular-nums text-surface-900",
                fieldRadius.control,
                fieldFocusVisibleClass,
                fieldInvalidClass,
                "disabled:cursor-not-allowed disabled:bg-surface-100 disabled:text-surface-500",
                error &&
                  "border-error-500 focus-visible:border-error-500 focus-visible:ring-error-500/20",
              )}
            />
          );
        })}
      </div>
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  );
}
